import { useEffect, useRef, useState } from "react";
import { Trophy, Zap } from "lucide-react";
import { api, fmt, curText } from "@/lib/api";
import { CoinIcon } from "@/components/Brand";

const TIER_STYLE = {
  grand: "from-[#3a1d00] via-[#5a2a00] to-[#2a1300] border-[#FFB800]/60 jp-grand-glow",
  major: "from-[#2a0f3d] to-[#170a24] border-[#a78bfa]/40",
  minor: "from-[#06283a] to-[#07141f] border-[#38bdf8]/40",
  mini: "from-[#0b2e1f] to-[#07160f] border-[#10B981]/40",
};

function useJackpots() {
  const [data, setData] = useState({ tiers: [], wins: [] });
  const [tick, setTick] = useState(0);
  const base = useRef(Date.now());
  useEffect(() => {
    const load = () => api.get("/jackpots").then((r) => { base.current = Date.now(); setData(r.data); }).catch(() => {});
    load();
    const poll = setInterval(load, 10000);
    const t = setInterval(() => setTick((x) => x + 1), 120);
    return () => { clearInterval(poll); clearInterval(t); };
  }, []);
  const elapsed = (Date.now() - base.current) / 1000;
  return { tiers: data.tiers.map((t) => ({ ...t, live: t.value + t.rate * elapsed })), wins: data.wins, tick };
}

function Tier({ t }) {
  const grand = t.id === "grand";
  return (
    <div data-testid={`jackpot-${t.id}`} className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${TIER_STYLE[t.id]} ${grand ? "col-span-3 lg:col-span-1 p-4 sm:p-5" : "p-2.5 sm:p-4"}`}>
      <div className="absolute inset-x-0 top-0 h-1.5 marquee-bulbs opacity-70" />
      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-black tracking-[0.25em] text-white/80">{grand && <Trophy size={14} className="gc-text" />}{t.name}</div>
      <div className={`mt-1 flex items-center gap-2 font-display font-black jp-value ${grand ? "text-3xl sm:text-4xl" : "text-[13px] sm:text-2xl gap-1 sm:gap-2"}`} data-testid={`jackpot-${t.id}-value`}>
        <CoinIcon cur="sc" size={grand ? 28 : 16} />
        {fmt(t.live, "sc")}
      </div>
    </div>
  );
}

function WinChip({ w, i }) {
  return (
    <div data-testid={`live-win-${i}`} className="shrink-0 flex items-center gap-2.5 rounded-xl bg-[#151A22] border border-white/5 pl-1.5 pr-3 py-1.5">
      {w.image ? <img src={w.image} alt="" className="w-8 h-10 rounded-md object-cover" /> : <span className="w-8 h-10 rounded-md bg-[#1F2633]" />}
      <div className="leading-tight">
        <div className="text-[11px] text-gray-400"><span className="text-white font-bold">{w.player}</span> · {w.game}</div>
        <div className={`text-sm font-black flex items-center gap-1 ${curText(w.currency)}`}>
          <CoinIcon cur={w.currency} size={13} />{w.currency === "usdc" ? "$" : ""}{fmt(w.win, w.currency)}
          <span className="ml-1 text-[10px] text-gray-500 font-bold">{w.multiplier}×</span>
        </div>
      </div>
    </div>
  );
}

export function JackpotTicker() {
  const { tiers, wins } = useJackpots();
  return (
    <section className="mt-6" data-testid="jackpot-ticker">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 font-display text-lg sm:text-xl font-black uppercase">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#EF4444]" /></span>
          Live Progressive Jackpots
        </h2>
        <span className="hidden sm:inline text-[11px] text-gray-500 font-bold uppercase tracking-wider">Growing every second</span>
      </div>
      <div className="grid grid-cols-3 lg:grid-cols-[1.6fr_1fr_1fr_1fr] gap-2.5 sm:gap-3">
        {tiers.map((t) => <Tier key={t.id} t={t} />)}
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-2xl bg-[#0f131b] border border-white/5 overflow-hidden" data-testid="live-wins">
        <div className="shrink-0 flex items-center gap-1.5 pl-3 py-3 text-[11px] font-black uppercase tracking-wider text-[#FFB800]"><Zap size={14} fill="currentColor" />Live Wins</div>
        <div className="relative flex-1 overflow-hidden py-2">
          {wins.length ? (
            <div className="marquee-track flex gap-2.5">
              {[...wins, ...wins].map((w, i) => <WinChip key={`${i < wins.length ? "a" : "b"}-${w.created_at}-${w.player}`} w={w} i={i} />)}
            </div>
          ) : (
            <div className="text-xs text-gray-500 py-2.5" data-testid="live-wins-empty">No big wins yet today — yours could be the first.</div>
          )}
        </div>
      </div>
    </section>
  );
}
